import React, { useEffect, useState } from 'react';
import TopNavigation from '../components/TopNavigation';
import { auth } from '../firebase';
import ImageWithFallback from '../components/ImageWithFallback';
import { uploadModeratedMedia } from '../utils/moderatedUpload';
import { useNavigate } from 'react-router-dom';
import { getBrandingConfig, getUserMe, logAdminAudit, setBrandingConfig } from '../utils/secureFunctions';

type BrandingConfig = {
  brandName: string;
  tagline: string;
  loginWelcome: string;
  signupWelcome: string;
  logoUrl?: string;
  authImageUrl?: string;
};

const DEFAULTS: BrandingConfig = {
  brandName: 'Belivah',
  tagline: 'INSPIRE. ENTERTAIN. CONNECT.',
  loginWelcome: 'Welcome back! Sign in to reconnect with your community and continue inspiring others.',
  signupWelcome: 'Welcome! Join a global community of believers. Share your story, connect, and be inspired.',
};

export default function AdminCustomizer() {
  const navigate = useNavigate();
  const [isAuthorized, setIsAuthorized] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<BrandingConfig>(DEFAULTS);
  const [previewKind, setPreviewKind] = useState<'draft' | 'published'>('draft');
  const [status, setStatus] = useState<string>('');

  useEffect(() => {
    (async () => {
      if (!auth.currentUser) { navigate('/login'); return; }
      try {
        const me = await getUserMe();
        const role = me.profile?.role || 'user';
        const ok = role === 'admin' || role === 'super_admin' || role === 'moderator';
        setIsAuthorized(ok);
        if (!ok) { navigate('/home'); return; }
      } catch {
        navigate('/home');
        return;
      } finally {
        setLoading(false);
      }
      await loadDraft();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDraft = async () => {
    try {
      const [draftRes, publishedRes] = await Promise.all([
        getBrandingConfig('draft'),
        getBrandingConfig('published'),
      ]);
      if (draftRes.config) {
        setForm({ ...DEFAULTS, ...(draftRes.config as any) });
        setPreviewKind('draft');
      } else if (publishedRes.config) {
        setForm({ ...DEFAULTS, ...(publishedRes.config as any) });
        setPreviewKind('published');
      } else {
        setForm(DEFAULTS);
      }
    } catch (e: any) {
      setStatus(e?.message || String(e));
    }
  };

  const uploadImage = async (file: File, kind: 'logoUrl' | 'authImageUrl') => {
    if (!auth.currentUser) return;
    const uid = auth.currentUser.uid;
    const ext = file.name.split('.').pop() || 'png';
    const filename = `${kind === 'logoUrl' ? 'logo' : 'auth'}_${Date.now()}.${ext}`;
    const upload = await uploadModeratedMedia({
      folder: 'branding',
      field: kind,
      file,
      filename,
      contentType: file.type || 'image/png',
      metadata: { uid, kind, source: 'admin-customizer' },
    });
    setForm(prev => ({ ...prev, [kind]: upload.objectKey } as any));
  };

  const saveDraft = async () => {
    try {
      setSaving(true);
      await setBrandingConfig('draft', form);
      await logAdminAudit({ action: 'branding_save_draft', payload: { fields: Object.keys(form) } });
      setStatus('Draft saved');
      setPreviewKind('draft');
    } catch (e: any) {
      setStatus(e?.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    try {
      setSaving(true);
      await setBrandingConfig('published', form);
      await logAdminAudit({ action: 'branding_publish', payload: { fields: Object.keys(form) } });
      setStatus('Published');
      setPreviewKind('published');
    } catch (e: any) {
      setStatus(e?.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: 24 }}>Loading…</div>;
  if (!isAuthorized) return null;

  return (
    <div style={styles.page}>
      <TopNavigation />
      <div style={styles.container}>
        <div style={styles.header}> 
          <h1 style={styles.title}>Customizer</h1>
          <p style={styles.subtitle}>Manage logo, taglines, and auth page content</p>
        </div>

        <div style={styles.grid}>
          <div style={styles.card}>
            <h3 style={styles.sectionTitle}>Branding</h3>
            <label style={styles.label}>Brand Name</label>
            <input style={styles.input} value={form.brandName} onChange={e => setForm({ ...form, brandName: e.target.value })} />
            <label style={styles.label}>Tagline</label>
            <input style={styles.input} value={form.tagline} onChange={e => setForm({ ...form, tagline: e.target.value })} />
            <label style={styles.label}>Logo</label>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <input type="file" accept="image/*" onChange={e => { const f = e.target.files?.[0]; if (f) uploadImage(f, 'logoUrl'); }} />
              {form.logoUrl && <ImageWithFallback src={form.logoUrl} alt="logo" style={{ height: 40, objectFit: 'contain' }} />}
            </div>
          </div>

          <div style={styles.card}>
            <h3 style={styles.sectionTitle}>Auth Pages</h3>
            <label style={styles.label}>Login Welcome Text</label>
            <textarea style={styles.textarea} value={form.loginWelcome} onChange={e => setForm({ ...form, loginWelcome: e.target.value })} />
            <label style={styles.label}>Signup Welcome Text</label>
            <textarea style={styles.textarea} value={form.signupWelcome} onChange={e => setForm({ ...form, signupWelcome: e.target.value })} />
            <label style={styles.label}>Auth Image (hero)</label>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <input type="file" accept="image/*" onChange={e => { const f = e.target.files?.[0]; if (f) uploadImage(f, 'authImageUrl'); }} />
              {form.authImageUrl && <ImageWithFallback src={form.authImageUrl} alt="auth" style={{ height: 60, objectFit: 'cover', borderRadius: 8 }} />}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, marginTop: 16, flexWrap: 'wrap' }}>
          <button disabled={saving} onClick={saveDraft} style={styles.primaryBtn}>Save Draft</button>
          <button disabled={saving} onClick={publish} style={styles.publishBtn}>Publish</button>
          <button disabled={saving} onClick={loadDraft} style={styles.linkBtn}>Reload</button>
          <span style={{ alignSelf: 'center', color: '#5B6E6E' }}>{status}</span>
        </div>

        <div style={{ marginTop: 28 }}>
          <h3 style={styles.sectionTitle}>Preview ({previewKind})</h3>
          <div style={styles.previewWrap}>
            <div style={styles.previewCard}>
              <div style={{ background: '#0B6B6F', color: '#fff', padding: 16, borderTopLeftRadius: 12, borderTopRightRadius: 12 }}>
                {form.logoUrl ? (
                  <ImageWithFallback src={form.logoUrl} alt="logo" style={{ height: 32, objectFit: 'contain' }} />
                ) : (
                  <h2 style={{ margin: 0 }}>{form.brandName}</h2>
                )}
                <div style={{ color: '#66EAD1', marginTop: 4 }}>{form.tagline}</div>
              </div>
              <div style={{ padding: 16 }}>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>Login</div>
                <div style={{ fontSize: 14, color: '#072B2C' }}>{form.loginWelcome}</div>
                {form.authImageUrl && <ImageWithFallback src={form.authImageUrl} alt="auth" style={{ width: '100%', marginTop: 12, borderRadius: 8 }} />}
              </div>
            </div>

            <div style={styles.previewCard}>
              <div style={{ background: '#0B6B6F', color: '#fff', padding: 16, borderTopLeftRadius: 12, borderTopRightRadius: 12 }}>
                {form.logoUrl ? (
                  <ImageWithFallback src={form.logoUrl} alt="logo" style={{ height: 32, objectFit: 'contain' }} />
                ) : (
                  <h2 style={{ margin: 0 }}>{form.brandName}</h2>
                )}
                <div style={{ color: '#66EAD1', marginTop: 4 }}>{form.tagline}</div>
              </div>
              <div style={{ padding: 16 }}>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>Signup</div>
                <div style={{ fontSize: 14, color: '#072B2C' }}>{form.signupWelcome}</div>
                {form.authImageUrl && <img src={form.authImageUrl} alt="auth" style={{ width: '100%', marginTop: 12, borderRadius: 8 }} />}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  page: { minHeight: '100vh', background: '#F5EDE1' },
  container: { maxWidth: 1100, margin: '0 auto', padding: 20, paddingTop: 80 },
  header: { marginBottom: 12 },
  title: { fontSize: 28, fontWeight: 700, color: '#072B2C', margin: 0 },
  subtitle: { color: '#5B6E6E', marginTop: 4 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, marginTop: 12 },
  card: { background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  sectionTitle: { margin: '0 0 12px 0', color: '#072B2C' },
  label: { display: 'block', marginTop: 8, marginBottom: 6, color: '#072B2C', fontWeight: 600 },
  input: { width: '100%', padding: 10, borderRadius: 8, border: '1.5px solid #17A2A9', fontSize: 14 },
  textarea: { width: '100%', padding: 10, borderRadius: 8, border: '1.5px solid #17A2A9', fontSize: 14, minHeight: 80 },
  primaryBtn: { padding: '10px 16px', background: '#2196f3', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 },
  publishBtn: { padding: '10px 16px', background: '#4CAF50', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 },
  linkBtn: { padding: '10px 16px', background: 'transparent', color: '#0B6B6F', border: '1px solid #0B6B6F', borderRadius: 8, cursor: 'pointer', fontWeight: 600 },
  previewWrap: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 },
  previewCard: { background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
};
